import { v4 as uuidv4 } from 'uuid';
import Zip from 'jszip';
import mime from 'mime';

import ServiceErrorWrapper from '$shared/helpers/decorators/ServiceErrorWrapper';
import UIErrorMessage from '$shared/constants/UIErrorMessage';
import { api } from '$shared/api/index';

class FileService {
  @ServiceErrorWrapper()
  async downloadFileAsync(path: string, fileName: string): Promise<void> {
    const blob = await api.getFileAsync(path);
    if (!blob) throw new Error(UIErrorMessage.BlobNotFound);

    const fileType = mime.getType(fileName);
    const fileUrl = await this.getDataUrlAsync(blob, fileType);
    this.download(fileName, fileUrl);
  }

  @ServiceErrorWrapper()
  async downloadReportAsZip(reportPaths: string[]): Promise<void> {
    const reportsData: Blob[] = await Promise.all(
      reportPaths.map((reportPath) => api.getFileAsync(reportPath))
    );

    const zip = new Zip();
    reportsData.forEach((reportData, index) => {
      const fileName = `${uuidv4()}_${reportPaths[index].split('/').pop()}`;
      zip.file(fileName, reportData);
    });
    const zipData = await zip.generateAsync({ type: 'blob' });
    this.downloadZip(zipData, 'reports.zip');
  }

  @ServiceErrorWrapper()
  async downloadFileInNewTabAsync(path: string, fileName: string): Promise<void> {
    const blob = await api.getFileAsync(path);
    return this.openInNewTab(fileName, blob);
  }

  @ServiceErrorWrapper()
  async getFileArrayBuffeAsync(path: string): Promise<ArrayBuffer> {
    const blob: Blob = await api.getFileAsync(path);
    const arrayBuffer = await blob.arrayBuffer();
    return arrayBuffer;
  }

  @ServiceErrorWrapper()
  async getFileBlobAsync(path: string): Promise<Blob> {
    return await api.getFileAsync(path);
  }

  @ServiceErrorWrapper()
  async downloadCSVAsync(blob: Blob, fileName: string): Promise<void> {
    const dataUrl = await this.getDataUrlAsync(blob, 'text/csv');
    return this.download(fileName, dataUrl);
  }

  @ServiceErrorWrapper()
  async downloadReportAsync(path: string): Promise<void> {
    const blob = await api.getFileAsync(path);
    const dataUrl = await this.getDataUrlAsync(blob, 'application/pdf');
    const fileName = `${path.split('/').splice(2, 1).join('')}_report.pdf`;
    this.download(fileName, dataUrl);
  }

  @ServiceErrorWrapper()
  async downloadReportFromBlobAsync(blob: Blob, fileName = ''): Promise<void> {
    const dataUrl = await this.getDataUrlAsync(blob, 'application/pdf');
    this.download(fileName, dataUrl);
  }

  @ServiceErrorWrapper()
  async getScreenshotAsync(path: string): Promise<string> {
    const blob = await api.getFileAsync(path);
    return this.getDataUrlAsync(blob, 'image/png');
  }

  @ServiceErrorWrapper()
  async getImageAsync(path: string): Promise<string> {
    const blob = await api.getFileAsync(path);
    const mimeType = mime.getType(path);
    return this.getDataUrlAsync(blob, mimeType);
  }

  public async getDataUrlAsync(data: Blob, type: string | null): Promise<string> {
    if (!type) {
      return URL.createObjectURL(data);
    }
    const buff = await data.arrayBuffer();
    return URL.createObjectURL(new Blob([buff], { type: `${type};base64` }));
  }

  private download(fileName: string, dataUrl: string) {
    const anchor = document.createElement('a');
    anchor.href = dataUrl;
    anchor.target = '_blank';
    anchor.download = fileName;
    anchor.click();
  }

  private async downloadZip(data: Blob, fileName: string): Promise<void> {
    const buff = await data.arrayBuffer();
    const dataUrl = URL.createObjectURL(new Blob([buff], { type: `application/zip;base64` }));
    this.download(fileName, dataUrl);
  }

  private async openInNewTab(fileName: string, data: Blob) {
    const fileType = mime.getType(fileName);
    const fileUrl = await this.getDataUrlAsync(data, fileType);
    window.open(fileUrl);
  }
}

export default new FileService();
